import { useRef } from "react";
import type { Item } from "../../../../api/menu";
import { CardLayoutPreview } from "../cardResize/CardLayoutPreview";
import { CardResizeGrips } from "../cardResize/CardResizeGrips";
import { ItemCard } from "./ItemCard";
import {
  CATALOG_CARD_RESIZE,
  catalogCardSizingOf,
  catalogGridClassOf,
  catalogGridStyleOf,
  useCatalogLayout,
  useCatalogLayoutPreview,
} from "./catalogLayout";
import { useCatalogNameLines } from "./useCatalogNameLines";

interface CatalogCardsProps {
  items: Item[];
  quantityOf: (itemId: number) => number;
  searchTerm: string;
  billDiscountPercent: number;
}

interface CatalogGridProps extends CatalogCardsProps {
  selectedItemId: number | null;
  /** Клетката на продукта — за стрелките (DOM позиции) и скрола до него. */
  setItemRef: (itemId: number, element: HTMLDivElement | null) => void;
  /** Клик по кутийката — индексът е в `items`. */
  onCardClick: (itemId: number, index: number) => void;
  onQuantityChange: (itemId: number, quantity: number) => void;
  tintOf: (item: Item) => string | null;
}

/**
 * Предварителният изглед при влачене на дръжка (`CardLayoutPreview`) —
 * отделен компонент, за да прерисува на всяка стъпка на размера само
 * копията, не и истинските кутийки.
 */
const CatalogGridPreview = ({ items, quantityOf, searchTerm, billDiscountPercent }: CatalogCardsProps) => {
  const pending = useCatalogLayoutPreview((layout) => layout);
  const previewRef = useRef<HTMLDivElement>(null);
  const sizing = catalogCardSizingOf(pending?.cardHeight ?? null);
  useCatalogNameLines(
    previewRef,
    sizing === "fixed",
    `${pending?.cardColumns}|${pending?.cardHeight}|${items[0]?.id}`,
  );
  if (!pending) return null;
  return (
    <CardLayoutPreview
      ref={previewRef}
      spec={CATALOG_CARD_RESIZE}
      gridClassName={catalogGridClassOf(pending)}
      gridStyle={catalogGridStyleOf(pending)}
      cards={items.map((item) => ({
        key: item.id,
        card: (
          <ItemCard
            item={item}
            searchTerm={searchTerm}
            quantity={quantityOf(item.id)}
            billDiscountPercent={billDiscountPercent}
            sizing={sizing}
          />
        ),
      }))}
    />
  );
};

/**
 * ГРИДЪТ на каталога („Плочки", само десктоп): един плосък грид с ВСИЧКИ
 * продукти по реда на `items` — без категорийни секции/разделители
 * (категорията остава като ФИЛТЪР през chips-овете). Размерът — като на
 * масите в „Списък": дръжките на избраната кутийка (`CardResizeGrips`)
 * сменят кутийките на ред и височината на всички, докато се влачи —
 * сиви пунктирани контури, пази се на компютъра (`catalogLayout.ts`). Без
 * избор — auto-fill колони по 205px (панелът вдясно яде ~27%) и височина по
 * съдържанието. Клетката (`@container/card`) е и мярката за кегела в
 * кутийката, и рефът за стрелките — истинският размер, без уголемяването на
 * избрания продукт.
 */
export const CatalogGrid = ({
  items,
  selectedItemId,
  setItemRef,
  onCardClick,
  quantityOf,
  onQuantityChange,
  searchTerm,
  billDiscountPercent,
  tintOf,
}: CatalogGridProps) => {
  const cardColumns = useCatalogLayout((layout) => layout.cardColumns);
  const cardHeight = useCatalogLayout((layout) => layout.cardHeight);
  const layout = { cardColumns, cardHeight };
  const sizing = catalogCardSizingOf(cardHeight);
  // Влачи се дръжка — истинските кутийки са скрити (но на място) под
  // предварителния изглед в новия размер.
  const previewing = useCatalogLayoutPreview((pending) => pending !== null);
  const gridRef = useRef<HTMLDivElement>(null);
  useCatalogNameLines(gridRef, sizing === "fixed", `${cardColumns}|${cardHeight}|${items[0]?.id}`);

  return (
    <div ref={gridRef} className={`relative ${catalogGridClassOf(layout)}`} style={catalogGridStyleOf(layout)}>
      {items.map((item, index) => {
        const selected = selectedItemId === item.id;
        return (
          <div
            key={item.id}
            ref={(element) => setItemRef(item.id, element)}
            // Избраната е уголемена и стъпва върху съседните — клетката ѝ е
            // отгоре заедно с дръжките.
            className={`@container/card relative flex min-w-0 ${selected ? "z-10" : ""} ${
              previewing ? "opacity-0" : ""
            }`}
          >
            <ItemCard
              item={item}
              selected={selected}
              onSelect={(itemId) => onCardClick(itemId, index)}
              searchTerm={searchTerm}
              quantity={quantityOf(item.id)}
              onQuantityChange={onQuantityChange}
              billDiscountPercent={billDiscountPercent}
              tint={tintOf(item)}
              sizing={sizing}
            />
            {selected && <CardResizeGrips spec={CATALOG_CARD_RESIZE} />}
          </div>
        );
      })}
      {previewing && (
        <CatalogGridPreview
          items={items}
          quantityOf={quantityOf}
          searchTerm={searchTerm}
          billDiscountPercent={billDiscountPercent}
        />
      )}
    </div>
  );
};
